import { createFileRoute, Link } from '@tanstack/react-router';
import { Header, Footer } from '@/components/marketing';
import { Button } from '@/components/ui/button';
import { enterprise } from '@/lib/marketing-data';
import {
  ArrowRight,
  Server,
  Key,
  FileSearch,
  BadgeCheck,
  Headphones,
  Clock,
  Building2,
  Shield,
  Check,
} from 'lucide-react';

export const Route = createFileRoute('/enterprise')({
  component: EnterprisePage,
});

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Server,
  Key,
  FileSearch,
  BadgeCheck,
  Headphones,
  Clock,
};

function EnterprisePage() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="pt-32 pb-16 sm:pt-40 sm:pb-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-2 lg:gap-24 items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border bg-muted px-4 py-1.5 mb-6">
                  <Building2 className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">Enterprise</span>
                </div>
                <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
                  {enterprise.headline}
                </h1>
                <p className="mt-6 text-xl text-muted-foreground">
                  {enterprise.subheadline}
                </p>
                <div className="mt-10 flex flex-col sm:flex-row gap-4">
                  <Link to={enterprise.cta.href}>
                    <Button size="lg" className="gap-2 w-full sm:w-auto">
                      {enterprise.cta.text} <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/pricing">
                    <Button
                      size="lg"
                      variant="outline"
                      className="w-full sm:w-auto"
                    >
                      View Pricing
                    </Button>
                  </Link>
                </div>
              </div>

              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 rounded-2xl blur-2xl" />
                <div className="relative rounded-xl border bg-card p-8 shadow-2xl">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                      <Shield className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="font-semibold">Enterprise Security</h2>
                      <p className="text-sm text-muted-foreground">
                        SOC 2 Type II Certified
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {[
                      'End-to-end encryption',
                      'SSO & SAML authentication',
                      'Audit logging & compliance',
                      'Data residency options',
                      'Dedicated infrastructure',
                    ].map((item) => (
                      <div key={item} className="flex items-center gap-3">
                        <Check className="h-5 w-5 text-primary" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center mb-16">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                Enterprise-Grade Features
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Everything you need to deploy ForgeNetwork at scale.
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {enterprise.features.map((feature) => {
                const Icon = iconMap[feature.icon] || Server;
                return (
                  <div
                    key={feature.title}
                    className="rounded-xl border bg-card p-6 hover:border-primary/50 transition-colors"
                  >
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="mt-4 text-lg font-semibold">{feature.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Compliance */}
        <section className="py-16 sm:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center mb-16">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                Compliance & Security
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                We meet the highest standards for security and compliance.
              </p>
            </div>

            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { title: 'SOC 2 Type II', description: 'Certified compliant' },
                { title: 'GDPR', description: 'EU data protection' },
                { title: 'HIPAA', description: 'Healthcare ready' },
                { title: 'ISO 27001', description: 'Information security' },
              ].map((cert) => (
                <div
                  key={cert.title}
                  className="rounded-xl border bg-card p-6 text-center"
                >
                  <BadgeCheck className="h-10 w-10 text-primary mx-auto" />
                  <h3 className="mt-4 font-semibold">{cert.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {cert.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl bg-primary p-8 sm:p-16 text-center">
              <h2 className="text-3xl font-bold text-primary-foreground sm:text-4xl">
                Ready to Scale?
              </h2>
              <p className="mt-4 text-lg text-primary-foreground/80 max-w-2xl mx-auto">
                Let's discuss how ForgeNetwork can meet your enterprise requirements.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/contact" search={{ type: 'enterprise' }}>
                  <Button
                    size="lg"
                    variant="secondary"
                    className="gap-2 bg-white text-primary hover:bg-white/90"
                  >
                    Contact Sales <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contact" search={{ type: 'demo' }}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white/30 text-primary-foreground hover:bg-white/10"
                  >
                    Schedule Demo
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
